/**
 * Import will remove at compile time
 */

import type { StackTraceInterface, ResolveMetadataInterface } from '@providers/interfaces/stack-provider.interface';

/**
 * Imports
 */

import { formatStack, getErrorMetadata } from '@providers/stack.provider';

/**
 * A base class for custom errors with enhanced stack trace formatting and source code information.
 *
 * @remarks
 * The `xBuildBaseError` class extends the native `Error` class, adding functionality to:
 * - Parse and store structured stack trace metadata via {@link ResolveMetadataInterface}
 * - Format stack traces with syntax highlighting and source mapping
 * - Provide enhanced console output through custom Node.js inspection
 *
 * This is particularly useful for debugging errors in compiled or transpiled code by providing
 * clearer information about the original source of the error, including
 * - Original source file paths (from source maps)
 * - Highlighted code snippets showing the error location
 * - Enhanced stack frame formatting with proper indentation
 *
 * @example
 * ```ts
 * class ValidationError extends xBuildBaseError {
 *   constructor(message: string, field: string) {
 *     super(message, 'ValidationError');
 *     this.reformatStack(this, { withFrameworkFrames: false });
 *   }
 * }
 *
 * throw new ValidationError('Invalid email format', 'email');
 * ```
 *
 * @see {@link formatStack} for stack formatting
 * @see {@link getErrorMetadata} for stack parsing
 * @see {@link StackTraceInterface} for formatting options
 * @see {@link ResolveMetadataInterface} for the metadata structure
 *
 * @since 2.0.0
 */

export abstract class xBuildBaseError extends Error {
    /**
     * Structured metadata from the parsed stack trace.
     *
     * @remarks
     * Contains the parsed stack information including
     * - Original source code snippet
     * - Line and column numbers
     * - Source file path (from source maps)
     * - Formatted stack frames
     * - Syntax-highlighted code
     *
     * This property is populated by calling {@link reformatStack}.
     *
     * @since 2.0.0
     */

    protected errorMetadata: ResolveMetadataInterface | undefined;

    /**
     * Pre-formatted stack trace string ready for display.
     *
     * @remarks
     * Contains the complete formatted output including
     * - Error name and message
     * - Syntax-highlighted code snippet (if available)
     * - Enhanced stack trace with proper indentation
     *
     * This is generated by {@link formatStack} and used by the custom
     * Node.js inspector for console output.
     *
     * @since 2.0.0
     */

    protected formattedStack: string | undefined;

    /**
     * Creates a new instance of the base error class.
     *
     * @param message - The error message describing the problem
     * @param name - The error type name; defaults to `'xBuildBaseError'`
     *
     * @remarks
     * This constructor:
     * - Properly sets up the prototype chain to ensure `instanceof` checks work for derived classes
     * - Captures the stack trace if supported by the runtime environment
     * - Sets the error name for identification
     *
     * **Important:** This is a protected constructor and should only be called by derived classes.
     * Subclasses should call {@link reformatStack} after construction to enable enhanced formatting.
     *
     * @example
     * ```ts
     * class DatabaseError extends xBuildBaseError {
     *   constructor(message: string, public readonly query: string) {
     *     super(message, 'DatabaseError');
     *     this.reformatStack(this);
     *   }
     * }
     * ```
     *
     * @since 2.0.0
     */

    protected constructor(message: string, name: string = 'xBuildBaseError') {
        super(message);

        // Ensure a correct prototype chain (important for `instanceof`)
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = name;

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, this.constructor);
        }
    }

    /**
     * Gets the structured stack trace metadata.
     *
     * @returns The parsed stack metadata, or `undefined` if {@link reformatStack} has not been called
     *
     * @remarks
     * Provides read-only access to the error's structured stack information,
     * which can be used for:
     * - Custom error logging
     * - Error reporting services
     * - Debugging tools
     * - Stack analysis
     *
     * @example
     * ```ts
     * try {
     *   throw new ValidationError('Invalid input');
     * } catch (error) {
     *   if (error instanceof xBuildBaseError) {
     *     const meta = error.metadata;
     *     console.log(`Error at ${meta?.source}:${meta?.line}:${meta?.column}`);
     *   }
     * }
     * ```
     *
     * @since 2.0.0
     */

    get metadata(): ResolveMetadataInterface | undefined {
        return this.errorMetadata;
    }

    /**
     * Custom inspect behavior for Node.js console output.
     *
     * @returns The enhanced formatted stack trace if available, otherwise falls back to the raw stack trace
     *
     * @remarks
     * This method is automatically called by Node.js when the error is logged to the console
     * using `console.log()`, `console.error()`, or `util.inspect()`.
     *
     * The formatted output includes:
     * - Colored and styled error name and message
     * - Syntax-highlighted code snippet showing the error location
     * - Enhanced stack frames with source-mapped paths
     *
     * @example
     * ```ts
     * const error = new ValidationError('Invalid data');
     * console.log(error); // Automatically uses this method for formatting
     * ```
     *
     * @see {@link https://nodejs.org/api/util.html#custom-inspection-functions-on-objects | Node.js Custom Inspection}
     *
     * @since 2.0.0
     */

    [Symbol.for('nodejs.util.inspect.custom')](): string | undefined {
        return this.formattedStack || this.stack;
    }

    /**
     * Parses the error stack trace and generates enhanced formatting with metadata.
     *
     * @param error - The error object to parse and format
     * @param options - Optional configuration for stack trace parsing and formatting
     *
     * @remarks
     * This method performs two operations:
     * 1. Parses the error's stack trace using {@link getErrorMetadata} to extract structured metadata
     * 2. Formats the metadata using {@link formatStack} to create a styled, human-readable output
     *
     * The parsed metadata is stored in {@link errorMetadata} and the formatted string in {@link formattedStack}.
     *
     * **Typical usage:** Call this method in the constructor of derived error classes to enable
     * enhanced stack trace formatting.
     *
     * @example
     * ```ts
     * class NetworkError extends xBuildBaseError {
     *   constructor(message: string, public readonly statusCode: number) {
     *     super(message, 'NetworkError');
     *     // Enable enhanced formatting without framework frames
     *     this.reformatStack(this, {
     *       withFrameworkFrames: false,
     *       withNativeFrames: true
     *     });
     *   }
     * }
     * ```
     *
     * @example
     * ```ts
     * class CustomError extends xBuildBaseError {
     *   constructor(message: string) {
     *     super(message, 'CustomError');
     *     // Use default options
     *     this.reformatStack(this);
     *   }
     * }
     * ```
     *
     * @see {@link formatStack} for formatting logic
     * @see {@link getErrorMetadata} for parsing logic
     * @see {@link StackTraceInterface} for available options
     *
     * @since 2.0.0
     */

    protected reformatStack(error: Error, options?: StackTraceInterface): void {
        this.errorMetadata = getErrorMetadata(error, options);
        this.formattedStack = formatStack(this.errorMetadata, error.name, error.message);
    }
}
